import React, { useEffect, useMemo, useRef, useState } from "react";
import { View, TextInput, Animated, Platform } from "react-native";

interface OTPInputProps {
  value: string[];
  onChange: (value: string[]) => void;
  length?: number;
  disabled?: boolean;
}

export const OTPInput: React.FC<OTPInputProps> = ({
  value,
  onChange,
  length = 6,
  disabled = false,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const inputRefs = useRef<Array<TextInput | null>>(Array(length).fill(null));
  const animatedValues = useRef<Animated.Value[]>([]);

  const safeValue = useMemo(
    () => Array.from({ length }, (_, i) => value[i] ?? ""),
    [value, length],
  );

  useEffect(() => {
    animatedValues.current = Array.from(
      { length },
      () => new Animated.Value(0),
    );
    inputRefs.current = Array(length).fill(null);
    setActiveIndex(0);
  }, [length]);

  // Only animate on active change (no focusing here; focus is done directly in handlers)
  useEffect(() => {
    const v = animatedValues.current[activeIndex];
    if (!v) return;

    Animated.sequence([
      Animated.timing(v, { toValue: 1, duration: 90, useNativeDriver: true }),
      Animated.timing(v, { toValue: 0, duration: 90, useNativeDriver: true }),
    ]).start();
  }, [activeIndex]);

  const focusIndex = (i: number) => {
    const next = Math.max(0, Math.min(i, length - 1));
    setActiveIndex(next);

    // More reliable than useEffect focus when typing fast
    requestAnimationFrame(() => {
      inputRefs.current[next]?.focus();
    });
  };

  const updateAt = (index: number, char: string) => {
    const next = [...safeValue];
    next[index] = char;
    onChange(next);
  };

  const handleChangeText = (text: string, index: number) => {
    if (disabled) return;

    const cleaned = text.replace(/\D/g, "");

    // Multi-char (paste / autofill / buffered fast typing)
    if (cleaned.length > 1) {
      const next = [...safeValue];

      for (let i = 0; i < cleaned.length; i++) {
        const target = index + i; // start at current box
        if (target >= length) break;
        next[target] = cleaned[i];
      }

      onChange(next);

      const lastFilled = Math.min(index + cleaned.length - 1, length - 1);
      focusIndex(lastFilled);
      return;
    }

    // Single char (or empty)
    const char = cleaned; // "" or "0-9"
    updateAt(index, char);

    if (char && index < length - 1) {
      focusIndex(index + 1); // move immediately (fixes "sometimes doesn't go next")
    }
  };

  const handleKeyPress = (event: any, index: number) => {
    if (disabled) return;

    if (event?.nativeEvent?.key === "Backspace") {
      if (safeValue[index]) {
        // clear current and stay
        updateAt(index, "");
        return;
      }
      if (index > 0) {
        focusIndex(index - 1);
      }
    }
  };

  const handleFocus = (index: number) => {
    if (disabled) return;
    setActiveIndex(index);
  };

  return (
    <View style={{ width: "100%" }}>
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          marginVertical: 20,
        }}
      >
        {safeValue.map((v, index) => {
          const scale =
            animatedValues.current[index]?.interpolate({
              inputRange: [0, 0.5, 1],
              outputRange: [1, 1.08, 1],
            }) ?? 1;

          return (
            <Animated.View
              key={index}
              style={{
                transform: [{ scale }],
                width: 50,
                height: 80,
                borderRadius: 12,
                backgroundColor: "white",
                shadowColor: "#000",
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.2,
                shadowRadius: 2,
                borderWidth: 2,
                borderColor: v ? "#4394e5" : "#d1d5db",
                marginHorizontal: 4,
              }}
            >
              <TextInput
                ref={(ref) => {
                  inputRefs.current[index] = ref;
                }}
                style={{
                  width: "100%",
                  height: "100%",
                  textAlign: "center",
                  fontSize: 28,
                  fontWeight: "bold",
                  color: "#111827",
                }}
                value={v}
                editable={!disabled}
                keyboardType="number-pad"
                // Allow paste/autofill only on the active box; others are single-char
                maxLength={activeIndex === index ? length : 1}
                onFocus={() => handleFocus(index)}
                onChangeText={(t) => handleChangeText(t, index)}
                onKeyPress={(e) => handleKeyPress(e, index)}
                placeholder="●"
                placeholderTextColor="#B0B0B0"
                selectTextOnFocus
                autoCorrect={false}
                autoCapitalize="none"
                textContentType={Platform.OS === "ios" ? "oneTimeCode" : "none"}
                autoComplete={
                  Platform.OS === "android" ? "sms-otp" : "one-time-code"
                }
              />
            </Animated.View>
          );
        })}
      </View>
    </View>
  );
};
